import { useCallback, useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import type {
  SearchFloatingPanelProps,
} from 'components/SearchV2/Components/Mobile/Panels/SearchFloatingPanel'
import SearchFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/SearchFloatingPanel'
import { GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import styled from 'styled-components'
import { rem } from 'polished'
import { addMonths, beginningOfToday } from 'lib/datetime/dateUtils'
import {
  INTL_SHORT_DAY_NAME_DAY_MONTH,
  INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import ButtonGroupToggle from 'components/Common/ButtonGroupToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import FlexibleDatePicker from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import {
  getDurationOptionsDefault,
} from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import type { FlightDropdownOption } from 'components/Flights/types'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import { mapFlexibleDatesToTypeaheadFormat } from '../../../../../lib/search/searchUtils'
import { useIntl } from 'react-intl'
import VerticalDateRangePicker from 'components/Common/Calendar/VerticalDateRangePicker'

const ContentContainer = styled(Group)`
  padding-top: ${rem(20)};
  height: 100%;
`

const DateSearchButtonGroup = styled(ButtonGroupToggle)`
  width: 100%;

  > * {
    flex: 1 1 0;
  }
`

interface Props
  extends Omit<
    SearchFloatingPanelProps,
    'title' | 'isConfirmButtonHidden' | 'children'
  > {
  checkinDate?: moment.Moment
  checkoutDate?: moment.Moment
  flexibleNights?: FLEXIBLE_DURATION_RANGE
  flexibleMonths?: string
  confirmButtonDisabled: boolean
  activeDateToggleButton?: {
    id: string
    label: string
  }
  dateToggleButtons?: Array<{
    id: string
    label: string
  }>
  onToggleDateSelection?: (item: any, e?: React.MouseEvent) => void
  handleDatesChanged?: (dates: {
    startDate: moment.Moment
    endDate?: moment.Moment | null
  }) => void
  hasDates: boolean
  minDate?: Date
  maxDate?: Date
  departureAirport?: App.AirportLocation
  arrivalAirport?: App.AirportLocation
  userSelectedFlexibleMonths?: boolean
  title?: string
  durationOptionsCustom?: Array<DurationOption>
  hideDatePicker?: boolean
  fareType?: FlightDropdownOption
  timeUnit?: 'day' | 'night'
  includeLastDayOnCount?: boolean
  showFlexibleYearSelector?: boolean
  allowSameDayRange?: boolean
  /** Called when user manually selects dates (for typeahead tracking) */
  onUserDatesChanged?: (dates: App.TypeaheadExtractedTravelDates) => void
}

function DateFloatingPanel(props: Props) {
  const intl = useIntl()
  const {
    checkinDate,
    checkoutDate,
    flexibleNights,
    flexibleMonths,
    confirmButtonDisabled,
    activeDateToggleButton,
    dateToggleButtons,
    onToggleDateSelection,
    handleDatesChanged,
    hasDates,
    minDate,
    maxDate,
    userSelectedFlexibleMonths = false,
    title,
    durationOptionsCustom,
    hideDatePicker = false,
    timeUnit = 'night',
    includeLastDayOnCount = false,
    showFlexibleYearSelector,
    allowSameDayRange,
    onUserDatesChanged,
    ...floatingPanelProps
  } = props

  const resolvedDurationOptions = durationOptionsCustom ?? getDurationOptionsDefault(intl)
  const bodyRef = useRef<HTMLDivElement>(null)
  const today = useMemo(() => beginningOfToday(), [])
  const defaultMaxDate = useMemo(() => addMonths(today, 36), [today])

  const defaultTitle = intl.formatMessage({
    id: 'uqiZQvy8R4R7YlRup4dj',
    defaultMessage: 'When do you want to go?',
    description: 'Default title for the date search floating panel in the search interface',
  })
  const resolvedTitle = title ?? defaultTitle

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  // Wrap handleDatesChanged to also track user selection for typeahead
  const wrappedHandleDatesChanged = useCallback((dates: {
    startDate: moment.Moment
    endDate?: moment.Moment | null
  }) => {
    handleDatesChanged?.(dates)
    // Track user selection for typeahead
    if (dates.startDate && dates.endDate) {
      onUserDatesChanged?.({
        type: 'specific',
        checkIn: dates.startDate.format(ISO_DATE_FORMAT),
        checkOut: dates.endDate.format(ISO_DATE_FORMAT),
      })
    }
  }, [handleDatesChanged, onUserDatesChanged])

  const handleFlexibleNightsChanged = useCallback(
    (newFlexibleNights: FLEXIBLE_DURATION_RANGE) => {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION, flexibleNights: newFlexibleNights })
      if (newFlexibleNights) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
      }
      // Track user selection for typeahead - use current flexibleMonths from props
      if (flexibleMonths && newFlexibleNights) {
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonths, newFlexibleNights))
      }
    },
    [globalSearchDispatch, flexibleMonths, onUserDatesChanged],
  )

  const handleFlexibleMonthOptionsChanged = useCallback(
    (flexibleMonthOptions: string) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
        flexibleMonths: flexibleMonthOptions,
      })
      if (flexibleMonthOptions.length > 0) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
        // Track user selection for typeahead - use current flexibleNights from props or default
        const nightsToUse = flexibleNights ?? DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonthOptions, nightsToUse))
      } else {
        globalSearchDispatch({
          type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS,
        })
      }
    },
    [globalSearchDispatch, flexibleNights, onUserDatesChanged],
  )

  const durationCount = hasDates && checkinDate && checkoutDate
    ? checkoutDate.diff(checkinDate, 'days') + (includeLastDayOnCount ? 1 : 0)
    : 0
  const durationLabel = timeUnit === 'day'
    ? intl.formatMessage(
        {
          id: 'LNVf4Nv-Q7npWFqUpxKu',
          defaultMessage: '{count, plural, one {# day} other {# days}}',
          description: 'Pluralised day count shown in the date picker summary',
        },
        { count: durationCount },
      )
    : intl.formatMessage(
        {
          id: 'vHXowfVjeAt-zoCLboDP',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Pluralised night count shown in date picker summary',
        },
        { count: durationCount },
      )

  const selectedDatesSummary = hasDates && checkinDate && checkoutDate ? (
    <Group
      direction="horizontal"
      verticalAlign="center"
      horizontalAlign="center"
      gap={8}
    >
      <BodyText as="time" variant="medium" weight="bold">
        <time dateTime={checkinDate.format(ISO_DATE_FORMAT)}>
          {intl.formatDate(checkinDate.toDate(), INTL_SHORT_DAY_NAME_DAY_MONTH)}
        </time>
      </BodyText>
      <LineArrowRightIcon size="M" />
      <BodyText variant="medium" weight="bold">
        <time dateTime={checkoutDate.format(ISO_DATE_FORMAT)}>
          {intl.formatDate(checkoutDate.toDate(), INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR)}
        </time>
      </BodyText>
      <Caption variant="large">
        {intl.formatMessage(
          {
            id: 'je7zYd7Vf-Lt7bhHzppA',
            defaultMessage: '({duration})',
            description: 'Duration label in parentheses on date floating panel',
          },
          { duration: durationLabel },
        )}
      </Caption>
    </Group>
  ) : undefined

  return (
    <SearchFloatingPanel
      {...floatingPanelProps}
      ref={bodyRef}
      title={resolvedTitle}
      confirmButtonDisabled={confirmButtonDisabled}
      footerContent={selectedDatesSummary}
    >
      <ContentContainer direction="vertical" verticalAlign="space-between">
        <VerticalSpacer gap={24}>
          {dateToggleButtons && (
            <DateSearchButtonGroup
              items={dateToggleButtons}
              activeItem={activeDateToggleButton}
              onItemClick={onToggleDateSelection}
              fitButtons
            />
          )}
          {(activeDateToggleButton?.id === DATE_SEARCH_OPTION_IDS.FLEXIBLE ||
            (hideDatePicker && activeDateToggleButton?.id === DATE_SEARCH_OPTION_IDS.ANYTIME)) && (
            <FlexibleDatePicker
              onMonthChange={handleFlexibleMonthOptionsChanged}
              onDurationChange={handleFlexibleNightsChanged}
              previouslySelectedDuration={flexibleNights}
              previouslySelectedMonths={userSelectedFlexibleMonths ? flexibleMonths : undefined}
              durationOptionsCustom={resolvedDurationOptions}
              showYearSelector={showFlexibleYearSelector}
            />
          )}
          {!hideDatePicker && activeDateToggleButton?.id !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <VerticalDateRangePicker
              startDate={checkinDate}
              endDate={checkoutDate}
              onDatesChange={wrappedHandleDatesChanged}
              min={minDate ?? today}
              max={maxDate ?? defaultMaxDate}
              contentRef={bodyRef}
              allowSameDayRange={allowSameDayRange}
            />
          )}
        </VerticalSpacer>
      </ContentContainer>
    </SearchFloatingPanel>
  )
}

export default DateFloatingPanel
